import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'react-feather';
import { Col, Card, CardBody, Collapse, Badge } from 'reactstrap';
import { Btn, H4, H6, Image, Spinner } from 'AbstractElements';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setFilters } from 'Features/Learning/CategoryProgram/categoryProgramSlice';
import { setField } from 'Features/Learning/CategoryProgram/addCategoryProgramSlice';
import PaginationComponent from '../Common/Pagination';
import {
  fetchCategory,
  handleEditCategoryProgram,
  handleDeleteCategoryProgram,
  handleBackPagination,
  handleNextPagination,
  handleClickPagination,
} from './CategoryProgramUtils';
import DualListBoxCategory from '../Common/DualListBoxCategory';
import Moment from 'moment';

const CategoryProgramProgramContain = ({ cardsOverride, searchQuery = '' }) => {
  const [expanded, setExpanded] = useState({});
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.CategoryProgram.filters);
  const isLoading = useSelector((state) => state.CategoryProgram.isLoading);
  const storeCards = useSelector((state) => state.CategoryProgram.data.data);
  const pagination = useSelector((state) => state.CategoryProgram.data.pagination);
  const [refatch, setRefatch] = useState(false);

  const cards = cardsOverride ?? storeCards;

  useEffect(() => {
    fetchCategory(dispatch, filters, refatch);
  }, [refatch]);

  useEffect(() => {
    if (!searchQuery?.trim() || !cards?.length) return;
    const query = searchQuery.trim().toLowerCase();
    const next = {};
    cards.forEach((item) => {
      const childMatch = item?.children?.some(
        (child) => child?.name?.toLowerCase().includes(query) || String(child?.id).includes(query)
      );
      if (childMatch) next[item.id] = true;
    });
    if (Object.keys(next).length) {
      setExpanded((prev) => ({ ...prev, ...next }));
    }
  }, [searchQuery, cards]);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    if (!cards?.length) return;
    const next = {};
    cards.forEach((item) => {
      if (item?.children?.length) next[item.id] = true;
    });
    setExpanded(next);
  };

  const collapseAll = () => setExpanded({});

  const renderActions = (item, { showReorder = false } = {}) => (
    <div className="category-program__actions-row">
      {showReorder && item?.children?.length > 0 && <DualListBoxCategory datas={item} />}
      <Link
        to={`${process.env.PUBLIC_URL}/category-program/add/${item.id}`}
        onClick={() => handleEditCategoryProgram(dispatch, item, setField)}
        className="category-program__icon-btn"
        title="Edit"
        aria-label={`Edit ${item.name}`}
      >
        <i className="fa fa-pencil" aria-hidden="true" />
      </Link>
      <button
        type="button"
        className="category-program__icon-btn category-program__icon-btn--danger"
        title="Hapus"
        aria-label={`Hapus ${item.name}`}
        onClick={() => handleDeleteCategoryProgram(dispatch, item.id, setRefatch)}
      >
        <i className="fa fa-trash-o" aria-hidden="true" />
      </button>
    </div>
  );

  const renderChildRow = (child) => (
    <div key={`child-${child?.id}`} className="category-program__child">
      <div className="category-program__child-rail" aria-hidden="true" />
      <div className="category-program__row category-program__row--child">
        <div className="category-program__identity">
          <div className="category-program__avatar category-program__avatar--sm">
            {child?.icon ? (
              <Image
                attrImage={{
                  className: 'img-fluid',
                  src: `${child.icon}`,
                  alt: '',
                }}
              />
            ) : (
              <span>{(child?.name || '?').charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="category-program__meta">
            <div className="category-program__title-line">
              <span className="category-program__id">#{child.id}</span>
              <span className="category-program__name">{child.name}</span>
            </div>
            <span className="category-program__submeta">
              Update {Moment(child.updated_at).format('DD MMM YYYY')}
              {child?.slug ? ` · ${child.slug}` : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderParentCard = (item) => {
    const childCount = item?.children?.length || 0;
    const isOpen = !!expanded[item?.id];

    return (
      <Card key={`category-${item?.id}`} className="category-program__card">
        <div className="category-program__row category-program__row--parent">
          <button
            type="button"
            className="category-program__expand"
            onClick={() => childCount > 0 && toggleExpand(item.id)}
            disabled={childCount === 0}
            aria-expanded={isOpen}
            aria-controls={`category-children-${item.id}`}
            title={childCount > 0 ? (isOpen ? 'Tutup sub-kategori' : 'Buka sub-kategori') : 'Tidak ada sub-kategori'}
          >
            {childCount > 0 ? (isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />) : (
              <span className="category-program__expand-dot" />
            )}
          </button>

          <div className="category-program__identity">
            <div className="category-program__avatar">
              {item?.icon ? (
                <Image
                  attrImage={{
                    className: 'img-fluid',
                    src: `${item.icon}`,
                    alt: '',
                  }}
                />
              ) : (
                <span>{(item?.name || '?').charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="category-program__meta">
              <div className="category-program__title-line">
                <span className="category-program__id">#{item.id}</span>
                <span className="category-program__name">{item.name}</span>
                <Badge color="light" className="category-program__badge" pill>
                  {childCount} sub
                </Badge>
              </div>
              <span className="category-program__submeta">
                Update {Moment(item.updated_at).format('DD MMM YYYY')}
                {item?.slug ? ` · ${item.slug}` : ''}
              </span>
            </div>
          </div>

          {renderActions(item, { showReorder: true })}
        </div>

        <Collapse isOpen={isOpen}>
          <div id={`category-children-${item.id}`} className="category-program__children">
            {childCount > 0 ? (
              item.children.map(renderChildRow)
            ) : (
              <p className="category-program__empty-children mb-0">Belum ada sub-kategori.</p>
            )}
          </div>
        </Collapse>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Col lg="12">
        <Card className="category-program__state">
          <CardBody className="text-center py-5">
            <div className="loader-box justify-content-center">
              <Spinner attrSpinner={{ className: 'loader-30' }} />
            </div>
            <H6 attrH6={{ className: 'sub-title mb-0 mt-2' }}>Memuat kategori program...</H6>
          </CardBody>
        </Card>
      </Col>
    );
  }

  if (!cards || cards.length === 0) {
    return (
      <Col lg="12">
        <Card className="category-program__state">
          <CardBody className="text-center py-5">
            <div className="category-program__empty-icon" aria-hidden="true">
              <i className="fa fa-folder-open-o" />
            </div>
            <H4 attrH4={{ className: 'mb-2' }}>
              {!storeCards ? 'Sumber data tidak tersedia' : searchQuery ? 'Tidak ada hasil pencarian' : 'Belum ada kategori'}
            </H4>
            <p className="text-muted mb-3">
              {searchQuery
                ? `Tidak ditemukan kategori untuk “${searchQuery}”.`
                : 'Tambah kategori program pertama untuk mulai mengelompokkan konten.'}
            </p>
            {storeCards && !searchQuery && (
              <Link to={`${process.env.PUBLIC_URL}/category-program/add`}>
                <Btn attrBtn={{ color: 'primary' }}>
                  <i className="fa fa-plus me-1" aria-hidden="true" />
                  Tambah Category Program
                </Btn>
              </Link>
            )}
          </CardBody>
        </Card>
      </Col>
    );
  }

  return (
    <>
      <Col lg="12" className="mb-2">
        <div className="category-program__list-controls">
          <span className="text-muted">
            Menampilkan <strong>{cards.length}</strong> kategori
            {searchQuery ? ' (hasil filter)' : ''}
          </span>
          <div className="category-program__list-controls-actions">
            <Btn attrBtn={{ color: 'light', size: 'sm', onClick: expandAll }}>Expand semua</Btn>
            <Btn attrBtn={{ color: 'light', size: 'sm', onClick: collapseAll }}>Collapse semua</Btn>
          </div>
        </div>
      </Col>

      <Col lg="12">
        <div className="category-program__list">{cards.map(renderParentCard)}</div>
      </Col>

      {pagination && (
        <Col lg="12" className="mt-3">
          <Card>
            <CardBody className="py-2">
              <PaginationComponent
                pagination={pagination}
                handleBackPagination={() =>
                  handleBackPagination(pagination, filters, dispatch, setFilters, setRefatch)
                }
                handleClickPagination={(page) =>
                  handleClickPagination(page, filters, dispatch, setFilters, setRefatch)
                }
                handleNextPagination={() =>
                  handleNextPagination(pagination, filters, dispatch, setFilters, setRefatch)
                }
              />
            </CardBody>
          </Card>
        </Col>
      )}
    </>
  );
};

export default CategoryProgramProgramContain;
